import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Quote, Star, ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { LANDING } from '@/constants/testIds';
import { BUSINESS } from '@/data/business';
import { useReveal } from '@/hooks/useReveal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fictitious testimonials — to be replaced by live Google reviews when GOOGLE_PLACES_API_KEY
// and GOOGLE_PLACE_ID env vars are configured on the backend.
const FAKE_REVIEWS = [
    {
        author: 'Karim B.',
        rating: 5,
        relative_time: 'il y a 2 semaines',
        text: "Boutique de confiance depuis des années. L'équipe connaît son sujet, les conseils sont précis et le flocage est nickel. On y trouve toutes les tailles, c'est rare sur le 93.",
    },
    {
        author: 'Sophie L.',
        rating: 5,
        relative_time: 'il y a 1 mois',
        text: "Commande de tenues pour notre équipe technique passée vendredi, livraison lundi matin. Pro, réactif et le rapport qualité-prix est imbattable. Merci à toute l'équipe de MANU VET.",
    },
    {
        author: 'Pascal D.',
        rating: 5,
        relative_time: 'il y a 3 semaines',
        text: "J'achète mes chaussures Diadora et mes pantalons Blaklader chez eux depuis l'ouverture. Toujours du stock, toujours un sourire. La boutique a vraiment du caractère.",
    },
];

const Stars = ({ value, size = 'sm' }) => {
    const cls = size === 'lg' ? 'h-5 w-5' : 'h-3.5 w-3.5';
    return (
        <span className="inline-flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
                <Star
                    key={i}
                    className={`${cls} ${i <= Math.round(value || 0) ? 'text-[#FFD500] fill-[#FFD500]' : 'text-white/20'}`}
                    strokeWidth={1.5}
                />
            ))}
        </span>
    );
};

export const Testimonials = () => {
    const [ref, visible] = useReveal();
    const [data, setData] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/reviews`);
                if (!cancelled) setData(data);
            } catch (e) {
                console.error('reviews fetch failed', e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const isLive = data?.configured && data?.source === 'google_places';
    const reviews = isLive ? data.reviews.slice(0, 6) : FAKE_REVIEWS;

    return (
        <section
            data-testid={LANDING.testimonials}
            ref={ref}
            className="relative py-20 sm:py-28 border-t border-white/5"
        >
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
                <div className={`${visible ? 'in-view' : ''} reveal flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10 sm:mb-12`}>
                    <div>
                        <span className="section-label">07 / Avis clients</span>
                        <h2 className="mt-4 font-display uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.9] text-white">
                            <span className="text-outline-yellow">Avis</span>
                            <br />
                            de la boutique
                        </h2>
                    </div>

                    <div className="flex flex-col items-start sm:items-end gap-3">
                        {isLive && data?.rating_average != null && (
                            <div className="flex items-center gap-3">
                                <Stars value={data.rating_average} size="lg" />
                                <span className="font-display text-3xl text-white">
                                    {Number(data.rating_average).toFixed(1)}
                                </span>
                                <span className="text-xs uppercase tracking-[0.22em] text-white/60">
                                    · Note Google
                                </span>
                            </div>
                        )}
                        <a
                            href={data?.place_url || BUSINESS.googleReviewsSearchUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group inline-flex items-center gap-3 px-5 py-3 bg-white text-black font-bold uppercase tracking-wider text-xs hover:bg-[#FFD500] transition-colors corner-cut"
                        >
                            <Star className="h-4 w-4 fill-[#FFD500] text-[#FFD500]" />
                            Voir tous les avis Google
                            <ArrowUpRight className="h-3.5 w-3.5 group-hover:rotate-45 transition-transform" />
                        </a>
                    </div>
                </div>

                <ReviewSlider reviews={reviews} visible={visible} />
            </div>
        </section>
    );
};

const ReviewSlider = ({ reviews, visible }) => {
    const [index, setIndex] = useState(0);
    const total = reviews.length;
    const perView = 3; // desktop
    const maxIndex = Math.max(0, total - perView);
    const step = 1;

    const go = (dir) => setIndex((i) => Math.min(maxIndex, Math.max(0, i + dir * step)));

    useEffect(() => {
        if (total <= perView) return undefined;
        const id = setInterval(() => {
            setIndex((i) => (i >= maxIndex ? 0 : i + 1));
        }, 5200);
        return () => clearInterval(id);
    }, [maxIndex, total]);

    return (
        <div className={`${visible ? 'in-view' : ''} reveal relative`}>
            <div className="relative overflow-hidden">
                <div
                    className="flex gap-4 sm:gap-5 transition-transform duration-[800ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                    style={{
                        transform: `translateX(calc(-${index} * (100% / ${total}) - ${index} * 0.75rem))`,
                        width: `${(total / perView) * 100}%`,
                    }}
                >
                    {reviews.map((r, i) => (
                        <article
                            key={i}
                            className="shrink-0 relative corner-cut bg-[#0F0F0F] border border-white/12 p-6 sm:p-7"
                            style={{ width: `calc((100% - ${(total - 1) * 1.25}rem) / ${total})` }}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <span className="inline-flex items-center gap-0.5">
                                    {[1, 2, 3, 4, 5].map((s) => (
                                        <Star
                                            key={s}
                                            className={`h-3.5 w-3.5 ${s <= Math.round(r.rating || 0) ? 'text-[#FFD500] fill-[#FFD500]' : 'text-white/20'}`}
                                            strokeWidth={1.5}
                                        />
                                    ))}
                                </span>
                            </div>
                            <Quote className="h-6 w-6 text-[#FFD500]/40 mb-3" />
                            <p className="text-sm text-white leading-relaxed line-clamp-6">
                                « {r.text} »
                            </p>
                            <div className="mt-6 pt-4 border-t border-white/10 flex items-center gap-3">
                                {r.profile_photo_url ? (
                                    <img
                                        src={r.profile_photo_url}
                                        alt=""
                                        className="h-9 w-9 rounded-full object-cover border border-white/10"
                                        loading="lazy"
                                    />
                                ) : (
                                    <span className="h-9 w-9 inline-flex items-center justify-center bg-[#FFD500]/10 border border-[#FFD500]/30 text-[#FFD500] font-display text-base">
                                        {r.author?.slice(0, 1) || 'A'}
                                    </span>
                                )}
                                <div className="font-semibold text-sm text-white">{r.author}</div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>

            {total > perView && (
                <div className="mt-6 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            aria-label="Avis précédent"
                            onClick={() => go(-1)}
                            disabled={index === 0}
                            className="h-11 w-11 inline-flex items-center justify-center bg-transparent border border-white/30 text-white hover:bg-[#FFD500] hover:text-black hover:border-[#FFD500] transition-colors corner-cut disabled:opacity-30"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            aria-label="Avis suivant"
                            onClick={() => go(1)}
                            disabled={index === maxIndex}
                            className="h-11 w-11 inline-flex items-center justify-center bg-[#FFD500] text-black hover:bg-white transition-colors corner-cut disabled:opacity-30"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>
                    </div>
                    <div className="flex items-center gap-1.5">
                        {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                aria-label={`Groupe d'avis ${i + 1}`}
                                onClick={() => setIndex(i)}
                                className={`h-1.5 transition-all duration-500 ${
                                    i === index ? 'w-8 bg-[#FFD500]' : 'w-2 bg-white/25 hover:bg-white/60'
                                }`}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
